import { useCallback } from 'react'
import { Search } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'

const SCRIPT_SRC = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'

interface DaumPostcodeResult {
  zonecode: string
  roadAddress: string
  jibunAddress: string
}

interface DaumPostcodeConstructor {
  new (options: { oncomplete: (data: DaumPostcodeResult) => void }): { open: () => void }
}

declare global {
  interface Window {
    daum?: { Postcode: DaumPostcodeConstructor }
  }
}

export interface PostcodeSelection {
  postalCode: string
  addressBase: string
}

export function mapPostcodeResult(result: DaumPostcodeResult): PostcodeSelection {
  return {
    postalCode: result.zonecode,
    addressBase: result.roadAddress.trim() ? result.roadAddress : result.jibunAddress,
  }
}

function loadPostcodeScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.daum?.Postcode) {
      resolve()
      return
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true })
      existing.addEventListener('error', () => reject(new Error('postcode script load failed')), { once: true })
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('postcode script load failed'))
    document.head.appendChild(script)
  })
}

interface Props {
  onSelect: (selection: PostcodeSelection) => void
  disabled?: boolean
}

export default function KakaoPostcode({ onSelect, disabled = false }: Props) {
  const handleClick = useCallback(async () => {
    try {
      await loadPostcodeScript()
      const Postcode = window.daum?.Postcode
      if (!Postcode) throw new Error('postcode constructor is unavailable')
      new Postcode({ oncomplete: (data) => onSelect(mapPostcodeResult(data)) }).open()
    } catch (error) {
      console.error('Kakao postcode open failed', error)
    }
  }, [onSelect])

  return (
    <Button type="button" variant="outline" onClick={handleClick} disabled={disabled}>
      <Search data-icon="inline-start" />
      주소 검색
    </Button>
  )
}
